import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent, CartAddEvent } from '@theme/events';

class SugarRushCartPage extends Component {
  #controller;
  #busy = false;
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('click', this.#click, options);
    this.addEventListener('change', this.#change, options);
    this.addEventListener('submit', this.#submit, options);
    window.addEventListener('pageshow', this.#pageShow, options);
    this.dataset.enhanced = 'true';
  }
  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }
  #pageShow = async (event) => {
    if (!event.persisted || this.#busy) return;
    try { await this.#render(); } catch { this.#error(this.dataset.error); }
  };
  #error(message) {
    const node = this.querySelector('[data-cart-error]');
    if (node) { node.textContent = message; node.hidden = false; }
  }
  #setBusy(busy) {
    this.#busy = busy;
    this.setAttribute('aria-busy', String(busy));
    for (const node of this.querySelectorAll('button,input,textarea')) {
      if (busy) {
        node.dataset.wasDisabled = String(node.disabled);
        node.disabled = true;
      } else if (node.dataset.wasDisabled !== undefined) {
        node.disabled = node.dataset.wasDisabled === 'true';
        delete node.dataset.wasDisabled;
      }
    }
  }
  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-qty-plus],[data-qty-minus],[data-cart-remove],[data-upsell-add]');
    if (!control) return;
    event.preventDefault();
    if (this.#busy) return;
    if (control.hasAttribute('data-upsell-add')) {
      if (!control.disabled) this.#mutate('add', { items: [{ id: Number(control.dataset.variantId), quantity: 1 }] }, control);
      return;
    }
    const row = control.closest('[data-line-key]');
    const input = row?.querySelector('[data-qty-input]');
    if (!input) return;
    let quantity = 0;
    if (!control.hasAttribute('data-cart-remove')) {
      quantity = Math.max(0, Number(input.value) + (control.hasAttribute('data-qty-plus') ? 1 : -1));
      input.value = String(quantity);
      if (this.dataset.autoUpdate !== 'true') return;
    }
    this.#mutate('change', { id: row.dataset.lineKey, quantity }, control);
  };
  #change = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-qty-input]') || this.dataset.autoUpdate !== 'true') return;
    if (!event.target.checkValidity()) { event.target.reportValidity(); return; }
    const row = event.target.closest('[data-line-key]');
    this.#mutate('change', { id: row.dataset.lineKey, quantity: Number(event.target.value) }, event.target);
  };
  #submit = (event) => {
    if (this.#busy) { event.preventDefault(); return; }
    if (event.submitter?.name === 'checkout') return;
    event.preventDefault();
    const updates = {};
    for (const row of this.querySelectorAll('[data-line-key]')) {
      const input = row.querySelector('[data-qty-input]');
      if (!input.checkValidity()) { input.reportValidity(); return; }
      updates[row.dataset.lineKey] = Number(input.value);
    }
    const note = this.querySelector('[name="note"]');
    this.#mutate('update', { updates, ...(note ? { note: note.value } : {}) }, event.submitter);
  };
  async #render(html) {
    const sectionId = this.dataset.sectionId;
    const open = [...this.querySelectorAll('details[open]')].map(node => node.closest('[data-line-key]')?.dataset.lineKey || node.dataset.cartDetails);
    if (!html) html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(window.location.href));
    await morphSection(sectionId, html, { mode: 'full', injectStylesheet: true });
    const fresh = document.getElementById(this.id);
    for (const node of fresh?.querySelectorAll('details') || []) {
      if (open.includes(node.closest('[data-line-key]')?.dataset.lineKey || node.dataset.cartDetails)) node.open = true;
    }
    return fresh;
  }
  async #mutate(action, payload, source) {
    if (this.#busy) return;
    const sectionId = this.dataset.sectionId;
    const sourceRow = source?.closest('[data-line-key]');
    const rowKey = sourceRow?.dataset.lineKey;
    const rowIndex = [...this.querySelectorAll('[data-line-key]')].indexOf(sourceRow);
    const selector = source?.hasAttribute('data-qty-plus') ? '[data-qty-plus]' : source?.hasAttribute('data-qty-minus') ? '[data-qty-minus]' : '[data-qty-input]';
    const status = this.querySelector('[data-cart-status]');
    const error = this.querySelector('[data-cart-error]');
    if (error) error.hidden = true;
    if (status) status.textContent = this.dataset.updating;
    const updatedMessage = this.dataset.updated;
    this.#setBusy(true);
    let applied = false;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/${action}.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: [sectionId], sections_url: window.location.pathname + window.location.search })
      });
      const result = await response.json();
      if (!response.ok || result.errors) throw new Error(typeof result.description === 'string' ? result.description : typeof result.errors === 'string' ? result.errors : this.dataset.error);
      applied = true;
      const cartResponse = action === 'add' ? await fetch(`${root}cart.js`, { cache: 'no-store' }) : null;
      const cart = cartResponse ? await cartResponse.json() : result;
      const EventType = action === 'add' ? CartAddEvent : CartUpdateEvent;
      this.dispatchEvent(new EventType(cart, sectionId, { source: 'sr-cart-page', itemCount: cart.item_count, sections: result.sections }));
      const fresh = await this.#render(result.sections?.[sectionId]);
      if (fresh) {
        fresh.removeAttribute('aria-busy');
        const live = fresh.querySelector('[data-cart-status]');
        if (live) live.textContent = updatedMessage;
        const rows = [...fresh.querySelectorAll('[data-line-key]')];
        const matchingRow = rows.find(row => row.dataset.lineKey === rowKey) || (action === 'change' && payload.quantity > 0 ? rows[rowIndex] : null);
        const focus = matchingRow?.querySelector(selector) || fresh.querySelector('[name="checkout"]') || fresh.querySelector('a');
        focus?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (applied) {
        try { await this.#render(); } catch { this.#error(this.dataset.error); }
      } else {
        this.#error(error.message || this.dataset.error);
        for (const input of this.querySelectorAll('[data-qty-input]')) input.value = input.defaultValue;
        if (status) status.textContent = '';
      }
    } finally {
      this.#setBusy(false);
    }
  }
}
if (!customElements.get('sr-cart-page')) customElements.define('sr-cart-page', SugarRushCartPage);
